/** @file updateInstance: one instance update (facing ticks, clock, events, frame, effects, upload, meshes). */
import { cameraPitchDelta } from '@zilo/pose3d-facet/pitch/cameraPitchDelta';
import type { Camera } from '@zilo/pose3d-render-core/contract';
import type { PoseParams } from '@zilo/pose3d-pose/contract';
import type { FiredEvent } from '@zilo/pose3d-pose/events/eventTypes';
import { facingStepsOf } from '@zilo/pose3d-pose/facing/facingStepsOf';
import { advanceClock } from '@zilo/pose3d-runtime-babylon/clip/advanceClock';
import type { PuppetEvent } from '@zilo/pose3d-runtime-babylon/clip/clipClockTypes';
import { clipStateOf } from '@zilo/pose3d-runtime-babylon/clip/clipStateOf';
import { toPuppetEvents } from '@zilo/pose3d-runtime-babylon/clip/toPuppetEvents';
import { updateEffectRuns } from '@zilo/pose3d-runtime-babylon/effect/updateEffectRuns';
import { writeEffects } from '@zilo/pose3d-runtime-babylon/effect/writeEffects';
import { frameList, type FrameHooks } from '@zilo/pose3d-runtime-babylon/instance/frameList';
import type { CameraSource, InstanceCore } from '@zilo/pose3d-runtime-babylon/instance/puppetInstanceTypes';

/** The facing tick (60 Hz, design 4.1) and the longest update step (a stalled tab does not jump). */
const TICK = 1 / 60;
const MAX_DT = 0.25;
/** How many recent events the state keeps for the HUD. */
const RECENT = 3;

/** Turns the facing once per whole 60 Hz tick; steps = the override, else pose `facingStepsOf` (clip, then puppet). */
function stepFacing(core: InstanceCore, dt: number): void {
  core.tick += dt;
  const steps = core.steps ?? facingStepsOf(core.puppet, core.clock?.clip ?? null);
  while (core.tick >= TICK - 1e-9) {
    core.facing = core.fns.turnFacing(core.facing, core.heading, steps, core.puppet.facing.turnRate);
    core.tick -= TICK;
  }
}

/** The events of `(prevTime, time]` (`[start, time]` right after `play`); none while paused (a scrub fires none). */
function firedEvents(core: InstanceCore): FiredEvent[] {
  const out = core.scratch.fired;
  out.length = 0;
  const c = core.clock;
  if (c === null || c.paused) return out;
  const fresh = core.events === 'fresh';
  core.events = 'normal';
  return core.fns.collectEvents(c.clip, core.rig, fresh ? null : c.prevTime, c.time, out);
}

/** The pose parameters of this update. */
function paramsOf(core: InstanceCore): PoseParams {
  const base: PoseParams = { bodyFacingDeg: core.facing, profile: core.profile, variantMask: core.variantMask, ikMode: core.ikMode };
  return core.weaponGroup === null ? base : { ...base, weaponGroup: core.weaponGroup };
}

/** The evaluate/place hooks: plants and event reports after evaluate, effect runs and entries after place. */
function hooksOf(core: InstanceCore, fired: FiredEvent[], params: PoseParams, camera: Camera, events: PuppetEvent[]): FrameHooks {
  return {
    afterEvaluate: (pose) => {
      core.plants = core.fns.updatePlants(core.plants, fired, pose, core.rig, params, core.origin);
      const reports = core.fns.reportEvents(fired, pose, core.rig, params, core.origin, core.scratch.reports);
      if (core.clock !== null) events.push(...toPuppetEvents(reports, core.clock.clip));
      return core.plants;
    },
    afterPlace: (list, count, pose) => {
      core.runs = updateEffectRuns(core.runs, core.puppet, events, core.now);
      return writeEffects(list, count, { runs: core.runs, puppet: core.puppet, pose, camera, profile: core.profile, entityPos: core.origin, now: core.now });
    },
  };
}

/** Raises CAMERA_MISMATCH once on entering a pitch outside `profile.camera.pitchRange`. */
function checkPitch(core: InstanceCore, camera: Camera): void {
  const outside = cameraPitchDelta(core.profile, camera.pitchDeg).outsideRange;
  if (outside && !core.outside) {
    core.onWarning({
      code: 'CAMERA_MISMATCH', path: '/camera/pitch', message: `camera pitch ${String(camera.pitchDeg)} is outside the profile's pitchRange`,
      params: { pitch: camera.pitchDeg, range: core.profile.camera.pitchRange },
    });
  }
  core.outside = outside;
}

/**
 * `dt` (capped at 0.25 s) turns the facing per 60 Hz tick, advances the clock, collects the events once per crossing,
 * runs `frameList` with the camera's contract (real yaw and pitch) and the entity origin, delivers the events in order,
 * checks the pitch range, uploads the corners and moves the joint meshes.
 */
export function updateInstance(core: InstanceCore, dtSeconds: number, cam: CameraSource): void {
  const dt = Math.min(Math.max(0, dtSeconds), MAX_DT);
  core.now += dt;
  stepFacing(core, dt);
  if (core.clock !== null) core.clock = advanceClock(core.clock, dt);
  const fired = firedEvents(core);
  const camera = cam.contract();
  const params = paramsOf(core);
  const events: PuppetEvent[] = [];
  core.scratch.reports.length = 0;
  core.last = frameList({
    bundle: core.bundle, puppet: core.puppet, state: core.clock === null ? null : clipStateOf(core.clock), params, camera,
    plants: core.plants, entityPos: core.origin, hooks: hooksOf(core, fired, params, camera, events),
  }, core.fns, core.scratch);
  for (const e of events) core.onEvent(e);
  if (events.length > 0) core.lastEvents = [...core.lastEvents, ...events].slice(-RECENT);
  checkPitch(core, camera);
  core.draw.upload(core.last.list, core.last.count, { camera, profile: core.profile, view: cam.view(), depthMode: core.depthMode });
  core.joints?.sync(core.last.pose, core.origin);
}
